import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import Header from "../Components/Header";
import UserCard from "../Components/UserCard";
import VenueCard from "../Components/VenueCard";
import CheckInCard from "../Components/CheckInCard";
import FriendsGoingOut from "../Components/GoingOut/FriendsGoingOut";
import { fetchFeed } from "../Services/FeedService";
import { getToken } from "../Services/authStore";
import type { FeedObj } from "../data/types";
import "../Styles/FeedPage.css";

/**
 * Det sociala flödet som ersatte radar-widgeten på kartan. Globalt tills
 * vidare — allt som händer på Beatspot, nyast först.
 *
 * /feed kräver inloggning. Utan token visade sidan tidigare "Could not
 * load the feed" och stannade där, vilket såg ut som ett fel i stället
 * för det den faktiskt var: en inloggningsvägg.
 */
export default function FeedPage() {
  const [feed, setFeed] = useState<FeedObj[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [needsLogin, setNeedsLogin] = useState(!getToken());

  useEffect(() => {
    let alive = true;

    if (!getToken()) {
      setNeedsLogin(true);
      setIsLoading(false);
      return;
    }

    fetchFeed()
      .then((items) => {
        if (alive) setFeed(items);
      })
      .catch((e) => {
        console.error("feed fetch failed", e);
        if (!alive) return;

        // En utgången token ser likadan ut som ingen token för den som
        // tittar: flödet är inte trasigt, man är utloggad.
        if (/missing_token|invalid_token|401/.test(String(e?.message ?? ""))) {
          setNeedsLogin(true);
        } else {
          setError("Could not load the feed.");
        }
      })
      .finally(() => {
        if (alive) setIsLoading(false);
      });

    return () => {
      alive = false;
    };
  }, []);

  const sorted = [...feed].sort((a, b) => b.time.getTime() - a.time.getTime());

  return (
    <main className="feed-page">
      <Header />

      <div className="feed-page-inner">
        <div className="feed-page-header">
          <p className="feed-page-eyebrow">Beatspot</p>
          <h1>Feed</h1>
          <span>Everything playing and shared right now.</span>
        </div>

        {/* Kvällen först. Den är färskvara — klockan fem finns den inte
            längre, och då är det flödet som står här igen. Sektionen
            ritar sig själv till ingenting när ingen vän är på gång. */}
        {!needsLogin && <FriendsGoingOut />}

        {isLoading && <p className="feed-page-status">Loading feed…</p>}

        {needsLogin && !isLoading && (
          <div className="feed-page-status feed-page-signin">
            <p>The feed is for signed-in listeners.</p>
            <Link className="feed-page-signin-link" to="/login">
              Sign in
            </Link>
          </div>
        )}

        {error && !needsLogin && <p className="feed-page-status">{error}</p>}

        {!isLoading && !error && !needsLogin && sorted.length === 0 && (
          <p className="feed-page-status">
            Nothing here yet. Check in somewhere or share a song to get it going.
          </p>
        )}

        <div className="feed-page-list">
          {sorted.map((item) => {
            // id:n kommer från olika tabeller, så typen ingår i nyckeln.
            const key = `${item.type}-${item.id}`;

            if (item.type === "checkin") {
              return <CheckInCard key={key} feedObj={item} />;
            }

            if (item.type === "user") {
              return <UserCard key={key} feedObj={item} />;
            }

            return <VenueCard key={key} feedObj={item} />;
          })}
        </div>
      </div>
    </main>
  );
}
